import { sdk } from '../sdk';
import { getErrorMessage } from '../utils/errorMessage';

export interface Region {
  _id: string;
  regionId: number;
  name: string;
  controllerId: string | {
    _id: string;
    name: string;
    cnic: string;
    phone: string;
  };
  allowedOffices: number;
  officeRange: {
    start: number;
    end: number;
  };
  createdAt: string;
  updatedAt: string;
  currentOffices?: number;
}

export interface RegionsResponse {
  success: boolean;
  data: Region[];
  message: string;
}

export interface RegionResponse {
  success: boolean;
  data: Region;
  message: string;
}

export interface CreateRegionData {
  name: string;
  controllerId: string;
  allowedOffices: number;
}

export interface UpdateRegionData extends Partial<CreateRegionData> {}

export class RegionsAPI {
  static async getAllRegions(params?: { page?: number; limit?: number; search?: string }): Promise<{ data: Region[]; pagination?: { page: number; limit: number; total: number; totalPages: number } }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      
      const url = `/api/v1/regions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await sdk.client.get(url);
      
      // If response is empty or undefined, return empty array
      if (!response) {
        return { data: [] };
      }
      
      // Check if the response has the expected structure
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch regions');
      }
      
      // Return data with pagination info if available
      return {
        data: response.data || [],
        pagination: response.pagination
      };
    } catch (error) {
      throw error;
    }
  }

  static async getRegionById(id: string): Promise<Region> {
    try {
      const response = await sdk.client.get(`/api/v1/regions/${id}`);
      
      if (!response) {
        throw new Error('No response received');
      }
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch region');
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  static async createRegion(data: CreateRegionData): Promise<Region> {
    try {
      const response = await sdk.client.post('/api/v1/regions', data);
      
      if (!response) {
        throw new Error('No response received');
      }
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to create region');
      }
      
      return response.data;
    } catch (error: any) {
      
      // Extract specific error message from backend response
      if (error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error?.response?.status === 400) {
        throw new Error('Invalid data provided. Please check your input and try again.');
      } else if (error?.response?.status === 409) {
        throw new Error('A region with this name already exists.');
      } else {
        throw new Error(getErrorMessage(error, 'Failed to create region. Please try again.'));
      }
    }
  }

  static async updateRegion(id: string, data: UpdateRegionData): Promise<Region> {
    try {
      const response = await sdk.client.put(`/api/v1/regions/${id}`, data);
      
      if (!response) {
        throw new Error('No response received');
      }
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to update region');
      }
      
      return response.data;
    } catch (error: any) {
      
      // Extract specific error message from backend response
      if (error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error?.response?.status === 400) {
        throw new Error('Invalid data provided. Please check your input and try again.');
      } else if (error?.response?.status === 404) {
        throw new Error('Region not found.');
      } else if (error?.response?.status === 409) {
        throw new Error('A region with this name already exists.');
      } else {
        throw new Error(getErrorMessage(error, 'Failed to update region. Please try again.'));
      }
    }
  }

  static async deleteRegion(id: string): Promise<void> {
    try {
      const response = await sdk.client.delete(`/api/v1/regions/${id}`);
      
      if (!response) {
        throw new Error('No response received');
      }
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete region');
      }
    } catch (error: any) {
      
      // Extract specific error message from backend response
      if (error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error?.response?.status === 400) {
        throw new Error('Cannot delete region. Region has offices assigned. Please reassign or delete the offices first.');
      } else if (error?.response?.status === 404) {
        throw new Error('Region not found.');
      } else {
        throw new Error(getErrorMessage(error, 'Failed to delete region. Please try again.'));
      }
    }
  }

  static async getRegionStats(): Promise<Region[]> {
    try {
      const response = await sdk.client.get('/api/v1/regions/stats');
      
      if (!response) {
        return [];
      }
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch region statistics');
      }
      
      return response.data || [];
    } catch (error) {
      throw error;
    }
  }

  static async searchRegions(query: string): Promise<Region[]> {
    try {
      const response = await sdk.client.get(`/api/v1/regions/search?q=${encodeURIComponent(query)}`);
      
      if (!response) {
        return [];
      }
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to search regions');
      }
      
      return response.data || [];
    } catch (error) {
      throw error;
    }
  }
}
